/// <amd-module name="Wit/BoardsHub/KanbanBoard/Components/Util" />

import { ITeam } from "Agile/Contracts/Common";
import { IReadonlyObservableArray, ObservableCollection } from "VSS/Core/Observable";
import { IExternalMenuItem, getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssLayoutManager, WrappedComponent, usePageContext } from "VSS/Platform/Layout";
import { Link } from "VSSUI/Link";
import { IMenuItem, MenuItemType } from "VSSUI/Menu";
import { IBoardsWorkItemService } from "Wit/BoardsHub/Common/IBoardsWorkItemService";
import { IChecklistItem, IObservableBoardsWorkItem, ITestItem } from "Wit/BoardsHub/Common/Interfaces";
import * as Resources from "Wit/BoardsHub/KanbanBoard/Resources";
import { IClientWorkItem } from "Wit/ClientWorkItemService/IClientWorkItem";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IKanbanBoardSettingsPanelProps } from "Wit/Common/KanbanBoardSettingsTypes/Components/KanbanBoardSettingsPanel.Props";
import { DeleteDialog } from "Wit/Common/WorkItemDialogs/Components/DeleteDialog";
import { IWorkItemFormDialogService } from "Wit/Common/WorkItemDialogs/Services/IWorkItemFormDialogService";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import * as React from "react";
import { IAnnotationService } from "../IAnnotationService";

/**
 * Handles a user clicking (or activating using the keyboard) a work item link.
 * Normally opens the item in a work item form dialog, but will open the work item in a new tab if the user indicates that desire.
 */
export function onWorkItemClick(
    pageContext: IVssPageContext,
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.KeyboardEvent<HTMLAnchorElement>,
    itemId: number,
    annotationItem?: IChecklistItem | ITestItem,
    annotationParent?: IObservableBoardsWorkItem,
    onDelete?: (workItemId: number) => void,
    onDismiss?: (workItemId: number) => void
) {
    if (
        e.ctrlKey ||
        e.metaKey ||
        e.shiftKey ||
        ((e as React.MouseEvent<HTMLAnchorElement, MouseEvent>).button !== undefined &&
            (e as React.MouseEvent<HTMLAnchorElement, MouseEvent>).button !== 0) ||
        e.defaultPrevented
    ) {
        return;
    }
    openWorkItemForm(pageContext, itemId, annotationItem, annotationParent, onDelete, onDismiss);
    e.preventDefault();
}

/**
 * Opens a work item form dialog for the given item id.
 */
export function openWorkItemForm(
    pageContext: IVssPageContext,
    itemOrId: IClientWorkItem | number,
    annotationItem?: IChecklistItem | ITestItem,
    annotationParent?: IObservableBoardsWorkItem,
    onDelete?: (workItemId: number) => void,
    onDismiss?: (workItemId: number) => void
) {
    let itemId: number;
    let boardItem: IClientWorkItem | undefined;

    if (typeof itemOrId === "number") {
        const boardsWorkItemService = pageContext.getService<IBoardsWorkItemService>("IBoardsWorkItemService");
        itemId = itemOrId;
        boardItem = boardsWorkItemService.get(itemId)?.value;
    } else {
        boardItem = itemOrId;
        itemId = boardItem.id;
    }

    // we want all IWorkItems that belong to the board to be loaded with the current board's extension,
    // which updates column field when state changes
    boardItem?.hydrate(pageContext, /*workItem*/ undefined, /*includeHistory*/ false);

    const workItemFormDialogService = pageContext.getService<IWorkItemFormDialogService>("IWorkItemFormDialogService");
    // if we have an IWorkItem, we want to pass it to the dialog, because it might be for a new item that doesn't have an id
    workItemFormDialogService.showWorkItemFormDialog(boardItem?.iWorkItem.value ?? itemId, workItemFormDialogSaveCompleteListener, {
        onDelete: () => {
            onAnnotationDelete(pageContext, annotationItem, annotationParent);
            onDelete?.(itemId);
        },
        onDismiss: () => {
            onDismiss?.(itemId);
        }
    });
}

function onAnnotationDelete(pageContext: IVssPageContext, item?: IChecklistItem | ITestItem, parent?: IObservableBoardsWorkItem) {
    if (!item || !parent) {
        return;
    }
    const annotationService = pageContext.getService<IAnnotationService>("IAnnotationService");
    if ((item as IChecklistItem).type) {
        annotationService.removeChecklistAnnotation(parent, item as IChecklistItem);
    } else {
        annotationService.removeTestAnnotation("", parent, item as ITestItem, true);
    }
}

export async function workItemFormDialogSaveCompleteListener(pageContext: IVssPageContext, workItem: IWorkItem, includeHistory?: boolean) {
    const boardsWorkItemService = pageContext.getService<IBoardsWorkItemService>("IBoardsWorkItemService");
    const item = boardsWorkItemService.get(workItem);

    if (!item) {
        return;
    }

    const originalParentId: number | undefined = item.value.getFields()[CoreFieldRefNames.Parent];
    // Update the item
    await item.refresh(pageContext, includeHistory);
    // did this item's parent change?
    item.updateParentTitle(pageContext, originalParentId, id => boardsWorkItemService.get(id));

    // is this item the parent of any children, and if so, do we need to update their parent titles?
    const itemTitle = item.value.getRequiredFieldValue<string>(CoreFieldRefNames.Title);
    boardsWorkItemService.updateParentFieldTitle(workItem.id, itemTitle);

    item.notify();
}

export function getDefaultMenuItems(pageContext: IVssPageContext, workItemId: number, onEditTitle: () => void): ObservableCollection<IMenuItem> {
    const menuItems = new ObservableCollection<IMenuItem>();
    menuItems.push([
        {
            id: "open",
            groupKey: "open-work-item",
            text: Resources.Open,
            rank: 10,
            iconProps: { ariaHidden: true, iconName: "ReplyMirrored" },
            onActivate: () => {
                openWorkItemForm(pageContext, workItemId);
            }
        },
        { id: "divider", itemType: MenuItemType.Divider },
        {
            id: "edit-title",
            groupKey: "manage-work-item",
            text: Resources.EditTitle,
            rank: 20,
            iconProps: { ariaHidden: true, iconName: "Edit" },
            onActivate: onEditTitle
        }
    ]);

    return menuItems;
}

export function getDeleteMenuItem(pageContext: IVssPageContext, workItemId: number, projectName: string, onDelete: () => void): IMenuItem {
    return {
        id: "delete",
        groupKey: "manage-work-item",
        text: Resources.Delete,
        rank: 40,
        iconProps: { ariaHidden: true, iconName: "Delete" },
        onActivate: () => {
            getDeleteDialog(pageContext, workItemId, projectName, onDelete);
        }
    };
}

export function getDeleteDialog(pageContext: IVssPageContext, workItemId: number, projectName: string, onDelete: () => void): void {
    return openDialog(pageContext, onDismiss => (
        <DeleteDialog
            onDismiss={onDismiss}
            selectedItems={[{ [CoreFieldRefNames.Id]: workItemId, [CoreFieldRefNames.TeamProject]: projectName }]}
            onDeleteItems={onDelete}
        />
    ));
}

const TeamContextDataProvider = "ms.vss-tfs-web.team-context-data-provider";
interface IContributedItemContext {
    id?: number;
    team?: ITeam;
    workItemType?: string;
}

export function addContributedMenuItems(
    pageContext: IVssPageContext,
    menuItems: ObservableCollection<IMenuItem>,
    workItemId: number,
    workItemType: string
): IReadonlyObservableArray<IMenuItem> {
    // contributed items
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const currentTeam = contributionService.getData<ITeam | undefined>(TeamContextDataProvider);

    const context: IContributedItemContext = {
        team: currentTeam,
        id: workItemId,
        workItemType: workItemType
    };

    const processExternalMenuItem = (menuItem: IMenuItem, contribution: IMenuItem | IExternalMenuItem) => {
        if ((menuItem as IExternalMenuItem).hidden) {
            return undefined;
        }

        return menuItem;
    };

    menuItems.push(getMenuItems(pageContext, "ms.vss-work-web.work-item-context-menu-owp", context, { processExternalMenuItem }, true));
    menuItems.push(getMenuItems(pageContext, "ms.vss-work-web.work-item-context-menu", context, { processExternalMenuItem }, true));

    return menuItems;
}

export function openDialog(pageContext: IVssPageContext, callout: (onDismiss: () => void) => JSX.Element): void {
    const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
    layoutManager.renderCallout(onDismiss => callout(onDismiss));
}

/**
 * If true, page should be rendered in a way suitable for embedding in e.g. MS Teams
 */
export function isEmbedded() {
    const url = new URL(window.location.href);
    return url.searchParams.get("embedded")?.toLocaleLowerCase() === "true";
}

export function InvalidBoardMessage({ settingsPanelProps }: { settingsPanelProps: Omit<IKanbanBoardSettingsPanelProps, "onDismiss"> }): JSX.Element {
    const pageContext = usePageContext();
    return (
        <>
            <span>{Resources.InvalidBoardConfigurationMessage}</span>
            <Link onClick={() => renderBoardSettingsPanel(pageContext, settingsPanelProps)}>{Resources.CorrectThisNowLink}</Link>
            <span>{Resources.MoreInfoMessage}</span>
            <Link href="https://go.microsoft.com/fwlink/?LinkId=286303" rel="noopener noreferrer" target="_blank">
                {Resources.ClickHereLink}
            </Link>
        </>
    );
}

export function renderBoardSettingsPanel(pageContext: IVssPageContext, props: Omit<IKanbanBoardSettingsPanelProps, "onDismiss">): void {
    pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout(onDismiss => (
        <WrappedComponent<IKanbanBoardSettingsPanelProps>
            dependencies={["ms.vss-work-web.kanban-board-settings-panel"]}
            wrappedType={"ms.vss-work-web.kanban-board-settings-panel-component"}
            wrappedProps={{
                ...props,
                onDismiss
            }}
        />
    ));
}
